import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Flex,
  Icon,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from '@kognitos/lattice';

import type { V1CodeManifest } from '@/generated/hooks/grimoire/models';
import { V1AutomationActivationState } from '@/generated/hooks/grimoire/models/v1AutomationActivationState';
import type { TAutomationStage } from '@/shared/components/AutomationStageBadge';
import { AopMapViewToggle } from '@/shared/components/AopMapViewToggle';

import {
  AutomationDocumentStatus,
  AutomationDocumentTitle,
  type TAutomationRunningState,
} from './AutomationDocumentTitle';

export type { TAutomationRunningState };

/** Tooltip delay in milliseconds for the title bar's icon buttons. */
const TITLE_TOOLTIP_DELAY_MS = 200;

interface IAutomationDocumentTitleBarProps {
  /** The automation this bar names and acts on. */
  automationId?: string;
  /** The automation's display name — the only place it appears on the page. */
  title?: string;
  /** Lifecycle stage, which drives the badge and the stage's meta text. */
  stage?: TAutomationStage;
  /**
   * Allows click-to-edit on the title. Defaults to `false` so a read-only or
   * version-history viewer can never fire the rename mutation. `RenameAutomation`
   * is checked on top of this, so callers pass the mode, not the permission.
   */
  isTitleEditable?: boolean;
  /** ISO timestamp of the last save, shown on the draft + blueprint stages. */
  lastSavedAt?: string;
  /** Published version number (without the `v`), shown on the published stage. */
  publishedVersion?: string;
  /** ISO timestamp of the publish, shown on the published stage. */
  publishedAt?: string;
  /**
   * Set while a run is in flight; replaces the stage's meta text. `executing`
   * draws a pulsing `--primary` dot, `awaiting-guidance` a static `--warning`
   * one.
   */
  running?: TAutomationRunningState;
  /**
   * Replaces the stage's meta line with content the caller owns — the
   * blueprint's building / build-ended / staged-review states, which belong to
   * the build rather than to a run and so cannot ride on `running`. Passed as
   * a node so the phase that owns those states keeps their copy and icons.
   */
  status?: ReactNode;
  /**
   * Extra controls for the right cluster, placed after version history so the
   * primary one lands rightmost. The blueprint sends the staged review's
   * Keep / Discard pair through here, so their labels, loading and disabled
   * rules stay with the phase that owns them.
   */
  actions?: ReactNode;
  /**
   * Current version, e.g. `4`. It is never visible text: the version-history
   * tooltip names it, and the delete dialog scopes its active-run warning by
   * it.
   */
  version?: string;
  /**
   * Opens version history. Omitted hides the control entirely, so the
   * affordance only appears where it actually does something.
   */
  onOpenVersionHistory?: () => void;
  /**
   * Export the automation as a zip, offered in the options menu. Omitted
   * hides the item.
   */
  onExport?: () => void;
  /**
   * The built automation's manifest, which enables "Export as Markdown" in
   * the options menu.
   */
  exportManifest?: V1CodeManifest;
  /** Used for the Markdown export filename when the manifest carries no title. */
  exportTitleFallback?: string;
  /**
   * Opens the whole-automation test workbench. Omit it to hide the affordance
   * entirely (e.g. flag off).
   */
  onOpenWorkbench?: () => void;
  /**
   * Switches between the document and the AOP map. Omit it to hide the
   * affordance entirely (e.g. flag off, or no manifest to draw).
   */
  onToggleMapView?: () => void;
  /** Whether the map is showing, which points the toggle back at the document. */
  isMapView?: boolean;
  /** Activation state, which dims the stage badge and adds a Deactivated one. */
  activationState?: V1AutomationActivationState;
  /**
   * Cancels the document scroller's 40px horizontal padding so the bar still
   * spans the card edge to edge from inside it. Turn it off on a surface that
   * mounts the bar *outside* that padding — the run page hangs it above an
   * unpadded scroller, where the bleed pushes the bar 40px past the card and
   * the card's `overflow-hidden` clips the title.
   */
  bleed?: boolean;
  className?: string;
}

/**
 * The document's own title bar: a glass strip sticky at the top of the
 * document scroller carrying the automation name, its stage, the options
 * menu, and on the right either the stage's meta text or the live run state,
 * then version history and whatever actions the caller sends.
 *
 * It belongs to the document rather than to the page header, so the same bar
 * shows up on the draft, the blueprint and the published document without
 * each of those pages re-deriving the title area.
 */
export function AutomationDocumentTitleBar({
  automationId,
  title,
  stage = 'draft',
  isTitleEditable = false,
  lastSavedAt,
  publishedVersion,
  publishedAt,
  running,
  status,
  actions,
  version,
  onOpenVersionHistory,
  onExport,
  exportManifest,
  exportTitleFallback,
  onOpenWorkbench,
  onToggleMapView,
  isMapView = false,
  activationState,
  bleed = true,
  className,
}: IAutomationDocumentTitleBarProps) {
  const { t: tAutomation } = useTranslation('automation');

  // The version number is tooltip-only: showing it as text next to the icon
  // reads as a control you can pick a version from, which this is not.
  const versionHistoryLabel = version
    ? tAutomation('automation.header.tooltip.versionHistoryAt', { version })
    : tAutomation('automation.header.tooltip.versionHistory');

  return (
    /* Spec 1: the bar is the document card's first child and spans it
     * edge-to-edge, so the negative margin has to match the card's 40px
     * inner padding. */
    <Flex
      align="center"
      justify="between"
      gap={3}
      className={cn(
        'sticky top-0 z-30 h-14 shrink-0 rounded-t-2xl border-b border-border/60 bg-card/75 px-4 shadow-[inset_0_1px_0_var(--card)] backdrop-blur-[20px] saturate-[1.4]',
        bleed ? '-mx-10 w-[calc(100%+5rem)]' : 'w-full',
        className,
      )}
      data-testid="document-title-bar"
      data-stage={stage}
    >
      <Flex
        align="center"
        gap={0.5}
        className="min-w-0 flex-auto overflow-hidden"
      >
        <AutomationDocumentTitle
          automationId={automationId}
          title={title}
          stage={stage}
          isTitleEditable={isTitleEditable}
          version={version}
          onExport={onExport}
          exportManifest={exportManifest}
          exportTitleFallback={exportTitleFallback}
          activationState={activationState}
          // shrink-0 on everything after the name is what makes the name the
          // one that ellipsises when the bar is narrow.
          titleClassName="max-w-[70%]"
        />

        {onOpenWorkbench && (
          <Tooltip delayDuration={TITLE_TOOLTIP_DELAY_MS} variant="primary">
            <TooltipTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                data-testid="test-automation-workbench-button"
                aria-label={tAutomation(
                  'automation.header.tooltip.testAutomation',
                )}
                onClick={onOpenWorkbench}
              >
                <Icon type="FlaskConical" size="sm" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {tAutomation('automation.header.tooltip.testAutomation')}
            </TooltipContent>
          </Tooltip>
        )}

        {onToggleMapView && (
          <AopMapViewToggle onToggle={onToggleMapView} isMapView={isMapView} />
        )}
      </Flex>

      <Flex align="center" gap={2} className="min-w-0">
        <AutomationDocumentStatus
          stage={stage}
          lastSavedAt={lastSavedAt}
          publishedVersion={publishedVersion}
          publishedAt={publishedAt}
          running={running}
          status={status}
        />

        {onOpenVersionHistory && (
          <Tooltip delayDuration={TITLE_TOOLTIP_DELAY_MS} variant="primary">
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon-xs"
                className="rounded-md text-muted-foreground hover:text-foreground"
                onClick={onOpenVersionHistory}
                aria-label={versionHistoryLabel}
                data-testid="document-version-button"
              >
                {/* 13px sits between lattice's size-3 and size-3.5. */}
                <Icon type="History" className="size-[13px]" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" align="end">
              {versionHistoryLabel}
            </TooltipContent>
          </Tooltip>
        )}

        {actions}
      </Flex>
    </Flex>
  );
}
